import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { ROUTES } from "config/navConfig";
import { MOCK_SPVS } from "data/mockData";

const STATUS_CONFIG = {
  active:          { label: "Active",        color: "var(--color-success)",          bg: "rgba(5,150,105,0.08)"   },
  fundraising:     { label: "Fundraising",   color: "var(--color-primary)",          bg: "rgba(59,76,76,0.08)"    },
  "pre-formation": { label: "Pre-Formation", color: "#d97706",                       bg: "rgba(217,119,6,0.08)"   },
  open:            { label: "Open",          color: "var(--color-success)",          bg: "rgba(5,150,105,0.08)"   },
  closing:         { label: "Closing",       color: "#d97706",                       bg: "rgba(217,119,6,0.08)"   },
  closed:          { label: "Closed",        color: "var(--color-primary)",          bg: "rgba(59,76,76,0.08)"    },
};

const fmtM = (n) => `£${(n / 1_000_000).toFixed(0)}m`;

export default function ActiveSPVsTable() {
  const navigate = useNavigate();

  // Show active and fundraising SPVs; pre-formation shown last
  const displaySPVs = [...MOCK_SPVS].sort((a, b) => {
    const order = { active: 0, fundraising: 1, "pre-formation": 2 };
    return (order[a.status] ?? 3) - (order[b.status] ?? 3);
  });

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Icon name="Briefcase" size={18} color="var(--color-primary)" />
          <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: "1rem" }}>
            Active SPVs &amp; Deals
          </h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          iconSize={14}
          onClick={() => navigate(ROUTES.spvCreation)}
        >
          New SPV
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-border">
              {["SPV Name", "Sector", "Target", "Raised", "Target IRR", "Investors", "Status"]?.map((h) => (
                <th
                  key={h}
                  className="px-5 py-3 text-left font-caption font-medium text-muted-foreground"
                  style={{ fontSize: "0.75rem", letterSpacing: "0.04em", textTransform: "uppercase" }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {displaySPVs?.map((spv) => {
              const statusCfg = STATUS_CONFIG[spv?.status] ?? STATUS_CONFIG.active;
              return (
                <tr
                  key={spv?.id}
                  onClick={() => navigate(ROUTES.spvDashboard, { state: { spvId: spv?.id } })}
                  className="hover:bg-muted transition-all duration-200 cursor-pointer"
                >
                  <td className="px-5 py-3.5">
                    <p className="font-caption font-semibold text-foreground text-sm">{spv?.name}</p>
                    <p className="font-caption text-muted-foreground" style={{ fontSize: "0.75rem" }}>
                      {spv?.assetSnapshot?.geography ?? spv?.jurisdiction}
                    </p>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="font-caption text-foreground text-sm">{spv?.sector}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="font-data text-foreground text-sm">{fmtM(spv?.targetSize)}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="font-data text-foreground text-sm">{fmtM(spv?.raisedAmount)}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className="font-data font-semibold text-sm"
                      style={{ color: "var(--color-success)" }}
                    >
                      {spv?.targetIRR}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1.5">
                      <Icon name="Users" size={13} color="var(--color-muted-foreground)" />
                      <span className="font-data text-foreground text-sm">{spv?.investorCount}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className="inline-flex items-center px-2.5 py-1 rounded-full font-caption font-medium"
                      style={{ fontSize: "0.75rem", color: statusCfg?.color, background: statusCfg?.bg }}
                    >
                      {statusCfg?.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-3 border-t border-border">
        <button
          onClick={() => navigate(ROUTES.spvDeals)}
          className="font-caption text-primary text-sm hover:underline transition-all"
        >
          View all SPVs &amp; Deals →
        </button>
      </div>
    </div>
  );
}
